import type {
  CellClassParams,
  TooltipCallbackFunc,
  TooltipCallbackParams,
  ValueFormatterParams,
} from "ag-grid-community"

import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { formatNumber } from "~/utils/formatting"
import { isLifecyclePhasedOut } from "~/utils/lifecycleUtils"
import { extractVariantSummaries } from "~/utils/productTableUtils"

/**
 * The variant ids behind a cell, which the SalesAndForecast/PreOrders/Returns tooltips take as
 * their subject. A leaf row's own node is not a group, so it reads as no ids and the tooltip
 * falls back to the row itself.
 */
export function leafVariantIds(
  params: TooltipCallbackParams<IVariantSummary>,
): string[] | undefined {
  return params.node?.allLeafChildren?.map((node) => node.data?.variantId ?? "")
}

/** `value`'s result, else the row's own `field`, so a leaf row the callback skips still opens the tooltip. */
export function tooltipOrField<TData>(
  value: TooltipCallbackFunc<TData>,
  field: keyof TData,
): TooltipCallbackFunc<TData> {
  return (params) => value(params) ?? params.data?.[field]
}

/** Reads a zero as nothing to report, for counts where zero and absent mean the same thing. */
export function numberOrDash(params: ValueFormatterParams<IVariantSummary>): string {
  return params.value ? formatNumber(params.value) : "-"
}

/** An empty cell rather than a dash, for columns that only speak up when they have something. */
export function blankWhenNull(params: ValueFormatterParams<IVariantSummary>): string {
  return params.value == null ? "" : formatNumber(params.value)
}

/** Muted text for a cell whose variants have all been phased out. */
export function mutedWhenPhasedOut(params: CellClassParams<IVariantSummary>): string | undefined {
  return isLifecyclePhasedOut(extractVariantSummaries(params)) ? "text-fg-muted" : undefined
}

/**
 * Says why a phased-out cell has nothing to show, and defers to `otherwise` when it does. The
 * message names the measure that is missing, since "phased out" alone reads as a status.
 */
export function phasedOutTooltip(
  message: string,
  otherwise?: (params: TooltipCallbackParams<IVariantSummary>) => unknown,
) {
  return (params: TooltipCallbackParams<IVariantSummary>): unknown =>
    isLifecyclePhasedOut(extractVariantSummaries(params))
      ? { unavailableMessage: message }
      : otherwise?.(params)
}
